import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { useOutcome } from '@/app/outcome-context'

import {
  type CreateAccountWFRequest,
  useCreateAccountMutation,
  useGetProvisioningStatusQuery,
} from './accountApi'

export interface CreateAccountHook {
  submitCreateAccount: (payload: CreateAccountWFRequest) => void
  isLoading: boolean
  isError: boolean
  error: unknown
}

export function useCreateAccount(): CreateAccountHook {
  const { t } = useTranslation()
  const { onAccountReady } = useOutcome()
  const [createAccount, { isLoading, isError, error }] =
    useCreateAccountMutation()
  const [accountId, setAccountId] = React.useState<number | null>(null)

  const { data: statusData, isError: isStatusError } =
    useGetProvisioningStatusQuery(accountId!, {
      skip: accountId === null,
      pollingInterval: 2000,
    })

  React.useEffect(() => {
    if (accountId === null || !statusData) return
    if (statusData.status === 'ready') {
      setAccountId(null)
      toast.success(t('ACCOUNT_CREATED_SUCCESSFULLY'))
      onAccountReady(statusData.account_id, 'ready')
    } else if (statusData.status === 'partial_error') {
      setAccountId(null)
      toast.warning(t('PROVISIONING_PARTIAL_ERROR'))
      onAccountReady(statusData.account_id, 'partial_error')
    }
  }, [statusData, accountId, onAccountReady, t])

  React.useEffect(() => {
    if (!isStatusError) return
    setAccountId(null)
    toast.error(t('ERROR_CREATING_ACCOUNT'))
  }, [isStatusError, t])

  const submitCreateAccount = React.useCallback(
    (payload: CreateAccountWFRequest) => {
      createAccount(payload)
        .unwrap()
        .then(result => {
          if ('account_id' in result) {
            setAccountId(result.account_id)
          } else {
            toast.error(t('ERROR_CREATING_ACCOUNT'))
          }
        })
        .catch(() => {
          toast.error(t('ERROR_CREATING_ACCOUNT'))
        })
    },
    [createAccount, t],
  )

  return {
    submitCreateAccount,
    isLoading: isLoading || accountId !== null,
    isError,
    error,
  }
}
